import React from "react";
import { usePreloadedImageSrc } from "../hooks/usePreloadedImageSrc";

interface StableImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "onError"> {
  src?: string;
  onSourceError?: (failedUrl: string) => void;
}

/**
 * Drop-in `<img>` wrapper that preloads new `src` values in the background
 * and only swaps the displayed src once the new image has finished loading.
 *
 * This prevents the visible flash that would otherwise occur when the parent
 * receives a refreshed signed URL for an image that's already on screen.
 * The first src is rendered without preload (there is nothing to flash
 * from). The `onSourceError` callback does not need to be memoized.
 */
export const StableImage: React.FC<StableImageProps> = ({
  src,
  onSourceError,
  ...imgProps
}) => {
  const displayedSrc = usePreloadedImageSrc({
    url: src,
    onError: onSourceError,
  });

  if (!displayedSrc) {
    return null;
  }

  return (
    <img
      {...imgProps}
      src={displayedSrc}
      onError={() => onSourceError?.(displayedSrc)}
    />
  );
};
